/**
 * The error model: RFC 9457 problem details.
 *
 * One schema per status rather than one schema with a `status` field, so that a client can narrow
 * by status and get the extension fields that status actually carries — `unsatisfiable` exists only
 * on 422, `retry_after` only on 429. A single permissive shape would let a client read a field that
 * its case does not have, which is how a "which constraint gives?" prompt ends up empty.
 *
 * `title` and `detail` are Vietnamese: they are shown to the user (spec P6). Nothing else in this
 * package is — field names, descriptions and the `type` URI are for machines and for engineers.
 */

import { z } from "zod";

/** Base URI for problem types. A URI and not a slug, per RFC 9457. */
export const ERROR_BASE_URI = "https://trippool.app/errors";

/**
 * The `type` URI for a slug, so no call site builds one by concatenation.
 *
 * Generic in the slug, and that is not decoration: `Problem` is a discriminated union whose members
 * declare `type` as a *literal*, so a constructor can only build one if the compiler knows which slug
 * it is. A non-generic signature returning the whole union would make every problem constructor fail
 * to typecheck, and the error model would have to be built from unchecked object literals.
 */
export function problemType<S extends ProblemSlug>(slug: S): `${typeof ERROR_BASE_URI}/${S}` {
  return `${ERROR_BASE_URI}/${slug}`;
}

export const ProblemSlug = z.enum([
  "validation",
  "unauthenticated",
  "forbidden",
  "not-found",
  "conflict",
  "constraints-unsatisfiable",
  "quota-exceeded",
  "provider-unavailable",
  "not-implemented",
]).meta({ id: "ProblemSlug", description: "The slug after the last slash of a problem `type` URI." });

export type ProblemSlug = z.infer<typeof ProblemSlug>;

/**
 * Fields the RFC allows on every problem: `detail` and `instance` are optional, and the extension
 * members are added per status below.
 */
const common = {
  title: z.string().describe("Short, human-readable summary in Vietnamese."),
  detail: z.string().optional().describe("Explanation for this occurrence, in Vietnamese."),
  instance: z.string().optional().describe("Path of the request that failed, e.g. /api/v1/places."),
};

/** A single field's validation failure. The `field` is a dotted path into the request body. */
export const FieldError = z.object({
  field: z.string(),
  message: z.string().describe("Vietnamese, ready to display next to the field."),
}).meta({ id: "FieldError", description: "One field's validation failure." });

/**
 * 400 — the request was malformed.
 *
 * `errors` is a list rather than a message because the client renders each next to its input. A
 * single flattened string would have to be parsed back apart by the client, and the client is not
 * the place to encode the server's field syntax.
 */
export const ValidationProblem = z.object({
  type: z.literal(problemType("validation")),
  status: z.literal(400),
  ...common,
  errors: z.array(FieldError),
}).meta({ id: "ValidationProblem", description: "400. The request body or query was rejected." });

export const UnauthenticatedProblem = z.object({
  type: z.literal(problemType("unauthenticated")),
  status: z.literal(401),
  ...common,
}).meta({ id: "UnauthenticatedProblem", description: "401. Missing, expired or revoked token." });

export const ForbiddenProblem = z.object({
  type: z.literal(problemType("forbidden")),
  status: z.literal(403),
  ...common,
}).meta({ id: "ForbiddenProblem", description: "403. Authenticated but not permitted." });

/**
 * 404 — unknown id, or soft-deleted and not visible to this user.
 *
 * The two cases share a status deliberately: distinguishing them would tell a caller that a row
 * exists, which is an information leak on a resource they cannot see.
 */
export const NotFoundProblem = z.object({
  type: z.literal(problemType("not-found")),
  status: z.literal(404),
  ...common,
}).meta({ id: "NotFoundProblem", description: "404. Unknown, or soft-deleted and not visible to this caller." });

export const ConflictProblem = z.object({
  type: z.literal(problemType("conflict")),
  status: z.literal(409),
  ...common,
}).meta({ id: "ConflictProblem", description: "409. Duplicate, e.g. a provider place already linked." });

/**
 * 422 — the planner cannot satisfy the constraint set.
 *
 * Not an error to paper over. `unsatisfiable` names which constraints give, because the client must
 * show the user which one to relax (AC-4.5); a 422 with only a message would leave the user to
 * guess, and guessing is what makes a planner feel arbitrary.
 */
export const ConstraintsUnsatisfiableProblem = z.object({
  type: z.literal(problemType("constraints-unsatisfiable")),
  status: z.literal(422),
  ...common,
  unsatisfiable: z.array(z.string()).describe("Constraint keys that cannot all hold at once."),
}).meta({
  id: "ConstraintsUnsatisfiableProblem",
  description: "422. The constraints cannot all be met; `unsatisfiable` names the ones that give.",
});

/**
 * 429 — the free-tier quota guard.
 *
 * `retry_after` in seconds. The free tiers are the only thing bounding growth cost (spec §10.5), so
 * hitting one is a normal operating condition rather than an incident, and the client should back
 * off rather than retry immediately.
 */
export const QuotaExceededProblem = z.object({
  type: z.literal(problemType("quota-exceeded")),
  status: z.literal(429),
  ...common,
  retry_after: z.int().describe("Seconds to wait before retrying."),
}).meta({ id: "QuotaExceededProblem", description: "429. Free-tier quota guard; includes `retry_after`." });

/**
 * 503 — an upstream provider is down, and the response says which.
 *
 * `degraded` lists the affected capabilities so the client shows the right banner (AC-6) instead of
 * a generic failure. Degrading a plan because the forecast is missing is a different message from
 * degrading it because routing is down, and the user can act on the difference.
 */
export const ProviderUnavailableProblem = z.object({
  type: z.literal(problemType("provider-unavailable")),
  status: z.literal(503),
  ...common,
  degraded: z.array(z.enum(["places", "routing", "weather"])).describe("Capabilities currently unavailable."),
}).meta({
  id: "ProviderUnavailableProblem",
  description: "503. An upstream provider is down; `degraded` names what is affected.",
});

/**
 * 501 — the route is declared in the contract but the server does not serve it yet.
 *
 * This is the honest answer while the API is built phase by phase. The alternative, not registering
 * the route, produces a 404 — which says the resource does not exist, when in fact it is specified
 * and a client has been generated with a method for it. A 501 says what is true: the shape is agreed,
 * the implementation is not there.
 *
 * It is intentionally absent from every route's documented errors. A 501 is a state of this
 * deployment, not part of the interface, and documenting it on all eighteen routes would put a
 * permanent, meaningless entry in the document.
 */
export const NotImplementedProblem = z.object({
  type: z.literal(problemType("not-implemented")),
  status: z.literal(501),
  ...common,
}).meta({ id: "NotImplementedProblem", description: "501. Declared in the contract, not yet served." });

/**
 * Every problem schema, in status order.
 *
 * A list rather than only a union, because the OpenAPI generator needs the response code each one
 * belongs to and a union does not carry one. Keeping both derived from this single list means a new
 * problem cannot be added to the type and forgotten in the generated document.
 */
export const PROBLEMS = [
  ValidationProblem,
  UnauthenticatedProblem,
  ForbiddenProblem,
  NotFoundProblem,
  ConflictProblem,
  ConstraintsUnsatisfiableProblem,
  QuotaExceededProblem,
  ProviderUnavailableProblem,
  NotImplementedProblem,
] as const;

/**
 * Problem schemas keyed by HTTP status, for the response section of the generated document.
 *
 * `satisfies` and not a bare annotation: the compiler then checks that the declared key matches the
 * `status` literal inside each schema. A map that disagreed with its own schemas would produce a
 * document advertising a 409 body on a 404, which is exactly the kind of drift the generated
 * contract exists to prevent.
 */
export const PROBLEM_BY_STATUS = {
  400: ValidationProblem,
  401: UnauthenticatedProblem,
  403: ForbiddenProblem,
  404: NotFoundProblem,
  409: ConflictProblem,
  422: ConstraintsUnsatisfiableProblem,
  429: QuotaExceededProblem,
  503: ProviderUnavailableProblem,
  501: NotImplementedProblem,
} satisfies Record<number, z.ZodType>;

export type ProblemStatus = keyof typeof PROBLEM_BY_STATUS;

/**
 * Every problem, discriminated on `status`.
 *
 * `status` and not `type`, because a client already has the status from the response line — using it
 * costs nothing and needs no URI parsing. A client that prefers to switch on `type` still can; both
 * are literals and the two always agree, since they are declared together on each schema above.
 */
export const Problem = z.discriminatedUnion("status", PROBLEMS).meta({
  id: "Problem",
  description: "RFC 9457 problem details. Every non-2xx response body.",
});

export type Problem = z.infer<typeof Problem>;
export type ValidationProblem = z.infer<typeof ValidationProblem>;
export type FieldError = z.infer<typeof FieldError>;
export type NotImplementedProblem = z.infer<typeof NotImplementedProblem>;
export type NotFoundProblem = z.infer<typeof NotFoundProblem>;
export type ConflictProblem = z.infer<typeof ConflictProblem>;
export type ForbiddenProblem = z.infer<typeof ForbiddenProblem>;
export type UnauthenticatedProblem = z.infer<typeof UnauthenticatedProblem>;
export type QuotaExceededProblem = z.infer<typeof QuotaExceededProblem>;
export type ProviderUnavailableProblem = z.infer<typeof ProviderUnavailableProblem>;
export type ConstraintsUnsatisfiableProblem = z.infer<typeof ConstraintsUnsatisfiableProblem>;
